import { Texture, type TextureFormat } from './texture';

/**
 * A reusable colour-only framebuffer. Textures can be swapped in and out, which
 * keeps the number of live FBO objects proportional to render passes rather than
 * to the number of layers in the document.
 */
export class Framebuffer {
  readonly handle: WebGLFramebuffer;
  private readonly gl: WebGL2RenderingContext;
  private attached: Texture | null = null;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    this.handle = gl.createFramebuffer()!;
  }

  get texture(): Texture | null {
    return this.attached;
  }

  attach(texture: Texture): this {
    const { gl } = this;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.handle);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture.handle, 0);
    this.attached = texture;
    return this;
  }

  /** Binds the framebuffer and matches the viewport to the attached texture. */
  bind(): void {
    const { gl } = this;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.handle);
    if (this.attached) gl.viewport(0, 0, this.attached.width, this.attached.height);
  }

  clear(r = 0, g = 0, b = 0, a = 0): void {
    const { gl } = this;
    this.bind();
    gl.clearColor(r, g, b, a);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  checkComplete(label: string): void {
    const { gl } = this;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.handle);
    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    if (status !== gl.FRAMEBUFFER_COMPLETE) {
      throw new Error(`Framebuffer "${label}" is incomplete: 0x${status.toString(16)}`);
    }
  }

  dispose(): void {
    this.gl.deleteFramebuffer(this.handle);
    this.attached = null;
  }
}

/**
 * Two render targets that trade places. The compositor needs this because blend
 * modes such as Multiply must *read* the backdrop while writing the new result,
 * which a single attachment cannot do.
 */
export class PingPong {
  private readonly format: TextureFormat;
  private readonly textures: [Texture, Texture];
  private readonly buffers: [Framebuffer, Framebuffer];
  private index = 0;

  constructor(
    gl: WebGL2RenderingContext,
    width: number,
    height: number,
    format: TextureFormat = 'rgba8',
  ) {
    this.format = format;
    this.textures = [
      Texture.create(gl, width, height, format, 'linear'),
      Texture.create(gl, width, height, format, 'linear'),
    ];
    this.buffers = [
      new Framebuffer(gl).attach(this.textures[0]),
      new Framebuffer(gl).attach(this.textures[1]),
    ];
  }

  /** The texture holding the result accumulated so far. */
  get source(): Texture {
    return this.textures[this.index];
  }

  /** The framebuffer to render the next pass into. */
  get target(): Framebuffer {
    return this.buffers[1 - this.index];
  }

  get targetTexture(): Texture {
    return this.textures[1 - this.index];
  }

  swap(): void {
    this.index = 1 - this.index;
  }

  reset(): void {
    this.index = 0;
  }

  resize(width: number, height: number): void {
    if (this.textures[0].width === width && this.textures[0].height === height) return;
    for (const texture of this.textures) texture.resize(width, height);
    // Re-attaching refreshes the driver's idea of the attachment dimensions.
    this.buffers[0].attach(this.textures[0]);
    this.buffers[1].attach(this.textures[1]);
  }

  get width(): number {
    return this.textures[0].width;
  }

  get height(): number {
    return this.textures[0].height;
  }

  get textureFormat(): TextureFormat {
    return this.format;
  }

  dispose(): void {
    for (const buffer of this.buffers) buffer.dispose();
    for (const texture of this.textures) texture.dispose();
  }
}
